import { Box, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'
import { FieldOptionDataFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import MoneyField from 'modules/core/components/Inputs/MoneyField'
import Select from 'modules/core/components/Inputs/Select'
import TextInput from 'modules/core/components/Inputs/Text'
import TextArea from 'modules/core/components/Inputs/TextArea'
import MultiSelect from 'modules/core/components/MultiSelect'
import Rating from 'modules/core/components/Rating'
import createComputed from 'modules/core/hooks/createComputed'
import i18n from 'modules/core/lib/i18n'
import validateNumberInput from 'modules/core/lib/validateNumberInput'

import { FieldTypes } from 'modules/custom_resources/store/collections/customFields/fields'

type MultiSelectProps = React.ComponentProps<typeof MultiSelect>

type ChangeFunction = () => void
interface OptionalParams {
  onChange?: ChangeFunction
  readonly?: boolean
}
interface Props {
  form: Form
  optional?: OptionalParams
  currency?: string
}

const useSingleChoiceOptions = createComputed((form: Form) => {
  return form
    .get('choice_options')
    .value.filter(
      (option: FieldOptionDataFragment) =>
        option.is_active || form.get('value').value === option.value
    )
    .map((option: FieldOptionDataFragment) => ({
      value: option.value,
      label: option.label,
      disabled: !option?.is_active,
    }))
})

const useMultipleChoiceOptions = createComputed((form: Form) => {
  return form.get('options').value.map((v: FieldOptionDataFragment) => ({
    value: v,
    label: v,
    selected: form.get('value').value.includes(v),
  }))
})

export const InputItem = observer(({ form, currency, optional = {} }: Props) => {
  const { onChange, readonly } = optional
  const onChangeCallback = onChange || (() => {})
  const singleChoiceOptions = useSingleChoiceOptions(form)
  const multipleChoiceOptions = useMultipleChoiceOptions(form)

  const onChangeToggle = () => {
    const field = form.get('value')
    field.set(!field.value)
  }

  const onChangeRating = (value: number) => {
    form.get('value').set(value)
  }

  const isReadonly = readonly || !form.get('editable').value

  const updateValue: MultiSelectProps['onSubmit'] = (updatedOptions) => {
    const field = form.get('value')
    field.value.length = 0 // emptying field value

    updatedOptions.forEach((option) => {
      if (option.selected) field.value.push(option.value)
    })
    field.set(field.value)
  }

  switch (form.get('field_type').value) {
    case FieldTypes.text:
      return (
        <TextInput
          mandatory={form.get('mandatory').value}
          readonly={isReadonly}
          label={form.get('name').value}
          field={form.get('value')}
          onChange={onChangeCallback}
        />
      )

    case FieldTypes.money:
      return (
        <MoneyField
          mandatory={form.get('mandatory').value}
          readonly={isReadonly}
          label={form.get('name').value}
          field={form.get('value')}
          onChange={onChangeCallback}
          currency={currency ?? ''}
        />
      )

    case FieldTypes.cents: {
      const mandatory = form.get('mandatory').value

      return (
        <TextInput
          mandatory={mandatory}
          readonly={isReadonly}
          label={form.get('name').value}
          field={form.get('value')}
          onChange={onChangeCallback}
          validation={(value) => validateNumberInput(value, !mandatory)}
        />
      )
    }

    case FieldTypes.date: {
      const mandatory = form.get('mandatory').value

      const withNullLabel = form.has('withNullLabel') ? form.get('withNullLabel').value : undefined

      const withNull = !mandatory && withNullLabel

      return (
        <DayPicker
          mandatory={mandatory}
          readonly={isReadonly}
          withNull={withNull}
          withNullLabel={withNullLabel}
          label={form.get('name').value}
          field={form.get('value')}
          placeholder={i18n.t('common.not_applicable')}
        />
      )
    }

    case FieldTypes.longText:
      return (
        <TextArea
          mandatory={form.get('mandatory').value}
          readonly={isReadonly}
          label={form.get('name').value}
          field={form.get('value')}
          onChange={onChangeCallback}
        />
      )

    case FieldTypes.checkbox:
      return (
        <TogglerCheckbox
          value={!!form.get('value').value}
          disabled={!form.get('editable').value}
          label={form.get('name').value}
          onChange={onChangeToggle}
        />
      )

    case FieldTypes.singleChoice:
      return (
        <Select
          mandatory={form.get('mandatory').value}
          none={i18n.t('settings.custom_fields.new.labels.choose')}
          readonly={isReadonly}
          label={form.get('name').value}
          field={form.get('value')}
          options={singleChoiceOptions.get()}
          showOriginalLabelText
        />
      )

    case FieldTypes.multipleChoice:
      return (
        <MultiSelect
          label={form.get('name').value}
          options={multipleChoiceOptions.get()}
          readonly={false}
          onSubmit={updateValue}
          getLabel={(count) => {
            if (count === 0) return `--${i18n.t('common.choose')}--`

            return i18n.tp('common.option_selected', {
              count,
            })
          }}
          getSubmitLabel={(count) =>
            i18n.tp('common.option_selected', {
              count,
            })
          }
        />
      )

    case FieldTypes.rating:
      return (
        <Box gap="s4">
          <Text.Normal size="200" weight="medium" color="grey800">
            {form.get('name').value}
          </Text.Normal>
          <Rating
            max={Number(form.get('max_value').value)}
            min={Number(form.get('min_value').value)}
            onClick={onChangeRating}
            readonly={readonly}
            value={+form.get('value').value}
          />
        </Box>
      )

    default:
      return null
  }
})
